import { useState, useRef, useEffect } from 'react';
import { MoreVertical, Pencil, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AdminAction {
  key: string;
  label: string;
  icon: typeof Pencil;
  onClick: () => void;
  variant?: 'default' | 'danger';
  show?: boolean;
}

interface AdminActionsMenuProps {
  actions: AdminAction[];
  className?: string;
}

export function AdminActionsMenu({ actions, className }: AdminActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const visibleActions = actions.filter(action => action.show !== false);

  if (visibleActions.length === 0) return null;

  return (
    <div className={cn('relative', className)} ref={menuRef}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-grey-600 transition-colors"
        title="Actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl shadow-elevated border border-grey-200 py-1 min-w-[160px]">
          {visibleActions.map((action) => (
            <button
              key={action.key}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                action.onClick();
                setIsOpen(false);
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left',
                action.variant === 'danger'
                  ? 'text-error hover:bg-error/5'
                  : 'text-secondary hover:bg-grey-50'
              )}
            >
              <action.icon className="w-4 h-4" />
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Pre-built action creators for common operations
export const createEditAction = (onEdit: () => void): AdminAction => ({
  key: 'edit',
  label: 'Edit',
  icon: Pencil,
  onClick: onEdit,
});

export const createArchiveAction = (onArchive: () => void, isArchived: boolean): AdminAction => ({
  key: 'archive',
  label: isArchived ? 'Unarchive' : 'Archive',
  icon: isArchived ? ArchiveRestore : Archive,
  onClick: onArchive,
});

export const createDeleteAction = (onDelete: () => void): AdminAction => ({
  key: 'delete',
  label: 'Delete',
  icon: Trash2,
  onClick: onDelete,
  variant: 'danger',
});


